import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Button, Box, Flex, Text, Avatar, HStack,
  Spinner, VStack, Icon,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import { fetchMatchedVendors } from '../../actions/review.actions';
import StarRating from '../reviews/StarRating';
import VerifiedBadge from '../reviews/VerifiedBadge';
import { buildImageUrl } from '../../utils/imageUrl';
import VendorDetailDrawer from './VendorDetailDrawer';
import { goToMessageChannel } from '../../utils/messageLinks';

/**
 * MatchedVendorsModal — shown right after a service request is created,
 * replacing the old redirect to /search. Lists vendors matched to the
 * request's city/state/serviceType, verified professionals first, sorted by
 * average rating. Selecting a professional opens a right-side drawer with
 * their public details + recent reviews (see VendorDetailDrawer). Footer
 * keeps a "View all in search" escape hatch.
 */
const MatchedVendorsModal = ({ isOpen, onClose, requestId, channelId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedVendor, setSelectedVendor] = useState(null);
  const { items, total, loading } = useSelector(
    (state) => state.reviews.matchedVendors,
  );

  useEffect(() => {
    if (isOpen && requestId) {
      setSelectedVendor(null);
      dispatch(fetchMatchedVendors(requestId, { size: 10 })).then((result) => {
        // Nobody to show: take the requester straight to the request's message
        // channel (created with the request) instead of an empty list.
        if (channelId && !(result?.success && result.data?.length)) {
          goToMessageChannel(channelId);
        }
      });
    }
  }, [dispatch, isOpen, requestId, channelId]);

  const handleClose = () => {
    setSelectedVendor(null);
    onClose();
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/search/?type=request&page=1');
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size={{ base: 'sm', md: 'xl' }} isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Matched professionals for your request
            {total > 0 && (
              <Text fontSize="sm" fontWeight="normal" color="gray.500">
                {total} professional{total === 1 ? '' : 's'} match your request — we
                will also notify them for you.
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loading && (
              <Flex justify="center" py={8}>
                <Spinner size="lg" />
              </Flex>
            )}
            {!loading && items.length === 0 && channelId && (
              <Text color="gray.500" py={4}>
                Opening your request&apos;s messages…
              </Text>
            )}
            {!loading && items.length === 0 && !channelId && (
              <Text color="gray.500" py={4}>
                No matching professionals found yet — we&apos;ll notify nearby
                professionals about your request.
              </Text>
            )}
            <VStack align="stretch" spacing={3}>
              {items.map((vendor) => (
                <Flex
                  key={vendor.userId}
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  align="center"
                  gap={3}
                  cursor="pointer"
                  _hover={{ bg: 'gray.50', borderColor: 'blue.300' }}
                  onClick={() => setSelectedVendor(vendor)}
                  role="button"
                  aria-label={`View details for ${vendor.firstName} ${vendor.lastName}`}
                >
                  <Avatar
                    size="md"
                    name={`${vendor.firstName || ''} ${vendor.lastName || ''}`}
                    src={buildImageUrl(vendor.imageUrl)}
                  />
                  <Box flex="1">
                    <HStack spacing={2}>
                      <Text fontWeight="semibold">
                        {vendor.firstName} {vendor.lastName}
                      </Text>
                      <VerifiedBadge isVerified={vendor.isVerified} />
                    </HStack>
                    <HStack spacing={1}>
                      <StarRating value={vendor.averageRating} size={3.5} />
                      <Text fontSize="sm" color="gray.600">
                        {Number(vendor.averageRating || 0).toFixed(1)} ({vendor.reviewCount || 0})
                      </Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.500">
                      {[vendor.companyName, vendor.city, vendor.state]
                        .filter(Boolean)
                        .join(', ')}
                    </Text>
                  </Box>
                  <Icon as={FaChevronRight} color="gray.400" boxSize={3} />
                </Flex>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              Close
            </Button>
            <Button colorScheme="blue" onClick={handleViewAll}>
              View all in search
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <VendorDetailDrawer
        vendor={selectedVendor}
        requestId={requestId}
        isOpen={!!selectedVendor}
        onClose={() => setSelectedVendor(null)}
      />
    </>
  );
};

export default MatchedVendorsModal;
